<template>
  <section class="containerInputDatePicker" id="inputDatePickerId" :class="{'error': isRequiredErrorVisible}" ref="inputDatePickerContainerRef">
    <label class="containerInputDatePicker__label" :class="{'required': required}">{{ label }}</label>
    <div class="containerInputDatePicker__input">
      <datepicker
        v-model="date"
        range
        multiCalendars
        :format="formatDate"
        :dayNames="['M', 'T', 'W', 'T', 'F', 'S', 'S']"
        :monthChangeOnScroll="false"
        autoApply
        :minDate="minDate"
        :enableTimePicker="false"
        position="left"
        noToday
        :autoPosition="false"
        teleport="#inputDatePickerId"
        preventMinMaxNavigation
        monthNameFormat="long"
        menuClassName="customDatePicker"
        @update:modelValue="setModelValue($event)"
        @open="setStyles(true)"
        @closed="setStyles()">
        <template #dp-input="{ value }">
          <section class="containerInputDatePicker__input--input">
            <span class="grey-icon-calendar containerInputDatePicker__input--calendarIcon" :class="{'active': isCalendarOpen}"></span>
            <span class="containerInputDatePicker__input--value" v-if="value">{{value}}</span>
            <span class="containerInputDatePicker__input--placeholder" v-else>MM/DD/YYYY - MM/DD/YYYY</span>
          </section>
        </template>
        <template #clear-icon="{ clear }">
          <span class="containerInputDatePicker__input--closeIcon grey-icon-close" @click.stop="clear" />
        </template>
      </datepicker>
    </div>
    <span class="errorMessage" v-if="isRequiredErrorVisible">This field cannot be empty</span>
  </section>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import moment from 'moment'

export default {
  name: 'inputDatePicker',
  components: {
    Datepicker
  },
  props: {
    minDate: {
      type: Date,
      default: () => { return new Date() }
    },
    label: {
      tpye: String,
      default: 'DATE'
    },
    required: {
      type: Boolean,
      default: false
    },
    modelValue: {
      type: Array
    },
    showErrors: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup (props, { emit }) {
    const store = useStore()
    const date = ref([])
    const isCalendarOpen = ref(false)
    const inputDatePickerContainerRef = ref(null)

    /**
     * @description Returns a boolean to indicate that this is a required field and it has not a valid value.
     */
    const isRequiredErrorVisible = computed(() => {
      return props.required && props.showErrors && !date.value?.length
    })

    onMounted(() => {
      setInitialModelValue()
    })

    /**
     * @description Formats dates to show on input
     * @param dateRange Array of two dates
     */
    function formatDate (dateRange) {
      const formatDateStart = moment(dateRange[0]).format('MM/DD/YYYY')
      const formatDateEnd = moment(dateRange[1]).format('MM/DD/YYYY')
      return `${formatDateStart} - ${formatDateEnd}`
    }

    /**
     * @description Set the model value using the selected date.
     * @param rangeSelected range dates selected by the user.
     */
    function setModelValue (rangeSelected) {
      let stringDates = []
      if (rangeSelected) {
        stringDates = rangeSelected.map((date) => {
          return moment(date).format('YYYY-MM-DD')
        })
      }
      emit('update:modelValue', stringDates)
    }

    /**
     * @description Set date initial value
     */
    function setInitialModelValue () {
      if (props.modelValue) {
        const parseDates = props.modelValue.map((dateString) => {
          return store.getters['opportunities/newUTCDate'](dateString)
        })
        date.value = parseDates
      } else {
        date.value = []
      }
    }

    /**
     * @description Set additional classes to style the calendar
     */
    function setAdditionalClasses () {
      const calendarCells = Array.from(document.getElementsByClassName('dp__calendar_item'))
      const clasess = ['dp__range_between', 'dp__date_hover_end', 'dp__date_hover_start']
      calendarCells.forEach((cell) => {
        if (clasess.some(className => cell.children[0].classList.contains(className))) {
          if (cell.previousElementSibling?.children[0].classList.contains('dp__range_start')) {
            cell.classList.add('dp__nextToStart')
          }
          if (cell.nextElementSibling?.children[0].classList.contains('dp__range_end')) {
            cell.classList.add('dp__previousToEnd')
          }
        }
      })
    }

    /**
     * @description Set calendar styles when it is open
     * @param flag flag to know if calendar is open or not
     */
    function setStyles (flag = false) {
      isCalendarOpen.value = flag
      if (flag) {
        const arrowButtons = Array.from(document.getElementsByClassName('dp__inner_nav'))

        arrowButtons.forEach(button => {
          button.addEventListener('click', () => {
            setTimeout(() => {
              setAdditionalClasses()
              addRangeClasses()
            }, 0)
          })
        })
        addRangeClasses()
        setAdditionalClasses()
      }
    }

    /**
     * @description Set calendar number classes
     */
    function addRangeClasses () {
      const calendarNumbers = Array.from(document.getElementsByClassName('dp__calendar_item'))
      calendarNumbers.forEach(cell => {
        cell.addEventListener('mouseover', () => {
          setAdditionalClasses()
        })
      })
    }

    return {
      date,
      formatDate,
      isCalendarOpen,
      setModelValue,
      isRequiredErrorVisible,
      setStyles,
      inputDatePickerContainerRef
    }
  }
}
</script>
